import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { Play, X } from "lucide-react";

const thumbFromId = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
const playerFromId = (id) =>
  `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;

export default function VideosPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "videos"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setVideos(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((video) => video.videoId));
        setLoading(false);
      },
      (err) => {
        console.error("Erreur Firestore Videos:", err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  return (
    <main className="bg-black text-white min-h-screen px-6 md:px-20 py-24">
      <p className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
        Videos
      </p>
      <h1 className="text-5xl md:text-8xl font-black uppercase leading-none mb-12">
        Dernieres sessions en images.
      </h1>

      {!loading && videos.length === 0 && (
        <div className="rounded-lg border border-white/10 bg-zinc-950 p-8 text-gray-400">
          Les videos arrivent bientot. En attendant, retrouve l'univers Unlimited Session sur les autres pages.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {videos.map((v) => (
          <article key={v.id} className="group overflow-hidden rounded-lg border border-white/10 bg-zinc-950 shadow transition hover:-translate-y-1 hover:border-red-500/60">
            <button
              type="button"
              onClick={() => setActive(v)}
              className="block w-full text-left"
              aria-label={`Lire la vidéo ${v.title || v.videoId}`}
            >
              <div className="relative aspect-video overflow-hidden bg-zinc-900">
                <img
                  src={v.thumbnail || thumbFromId(v.videoId)}
                  alt={v.title || "Video Unlimited Session"}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 grid place-items-center bg-black/20 opacity-95 transition group-hover:bg-black/5">
                  <span className="grid h-14 w-14 place-items-center rounded-full bg-white text-black shadow-xl transition group-hover:scale-105 group-hover:bg-red-600 group-hover:text-white">
                    <Play size={24} fill="currentColor" />
                  </span>
                </div>
              </div>
            </button>
            <div className="p-4">
              <h2 className="text-lg font-semibold line-clamp-2">{v.title || "Video Unlimited Session"}</h2>
              <button
                type="button"
                onClick={() => setActive(v)}
                className="mt-4 rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-300 transition hover:border-white hover:bg-white hover:text-black"
              >
                Regarder sur le site
              </button>
            </div>
          </article>
        ))}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4 backdrop-blur-md"
          onMouseDown={() => setActive(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Lecteur vidéo intégré"
        >
          <div
            className="relative w-full max-w-5xl overflow-hidden rounded-lg border border-white/10 bg-black shadow-2xl"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="aspect-video">
              <iframe
                src={playerFromId(active.videoId)}
                title={active.title || "YouTube video player"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
            <button
              type="button"
              onClick={() => setActive(null)}
              className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-black/65 text-white backdrop-blur transition hover:bg-white hover:text-black"
              aria-label="Fermer la vidéo"
            >
              <X size={19} />
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
